import type { ReactElement } from "react";
import { HoverWords } from "../bar/hover-words.tsx";
import { StaffBadge, type StaffBadgeBox } from "../../ui/kit/index.ts";
import { useIcons } from "../icons/context.tsx";
import { badgeLook, staffBadgeArt } from "./look.ts";
import { badgeName } from "./words.ts";

/**
 * A staff badge as the page draws it: the kit's badge for a badge number, its art from the page's icons (ADR-111
 * decisions 12 and 16), and nothing for a number the page does not know or while the art is not there to draw (ADR-112
 * decision 4.3: there is no glyph look).
 *
 * @param props The `badge` number, as the status byte carries it, and the `box` it is drawn in.
 *   `tight` takes the badge's transparent room away on both sides.
 *   `words` wraps the badge in `HoverWords`, a tab stop that a click holds open, so its word shows in the HUD's tooltip; off by default, since the wrapper is a box of its own and a surface that shows the word itself (the chat log) or lays the badge out by its own box needs none.
 * @returns The badge, or null for no badge or no art yet.
 * @example
 * <PageStaffBadge badge={7} box={16} />
 */
export function PageStaffBadge({ badge, box, tight = false, words = false }: { readonly badge: number; readonly box: StaffBadgeBox; readonly tight?: boolean; readonly words?: boolean }): ReactElement | null {
  const icons = useIcons();
  const look = badgeLook(badge);
  if (look === null) {
    return null;
  }
  const art = staffBadgeArt(icons.snapshot, look, box, icons.log);
  if (art === null) {
    return null;
  }
  const badgeWord = badgeName(look);
  const drawn = <StaffBadge name={badgeWord} box={box} scale={icons.snapshot.scale} art={art} tight={tight} />;
  return words ? <HoverWords words={badgeWord} pin>
      {drawn}
    </HoverWords> : drawn;
}
